import { create } from 'zustand';

/**
 * License store — tracks which shop items the current user owns,
 * and which item IDs exist in the shop (i.e. require a license to use).
 * Non-persisted: cleared on sign-out, reloaded on sign-in.
 */
export const useLicenseStore = create((set, get) => ({
  // Set of owned itemIds
  licenses: new Set(),
  isLoaded: false,
  isLoading: false,

  // Set of itemIds that exist in the shop (may require purchase)
  // Keyed by itemId — value is the full shop item object
  shopItemMap: {},
  shopItemsLoaded: false,

  hasLicense: (itemId) => {
    if (!itemId) return false;
    return get().licenses.has(itemId);
  },

  /** Returns true if this ID is a shop-gated item that costs money */
  isPaidItem: (itemId) => {
    if (!itemId) return false;
    const item = get().shopItemMap[itemId];
    return !!item && item.price > 0 && !item.discoverable;
  },

  /** Returns true if this ID is a redeemable item (unlockable via code) */
  isRedeemableItem: (itemId) => {
    if (!itemId) return false;
    const item = get().shopItemMap[itemId];
    return !!item && !!item.redeemable;
  },

  /** Returns true if this ID is in the shop at all (free or paid) */
  isShopItem: (itemId) => {
    if (!itemId) return false;
    return itemId in get().shopItemMap;
  },

  loadLicenses: async () => {
    if (get().isLoading) return;
    set({ isLoading: true });
    try {
      const { shopApi } = await import('../services/shopApi');
      const data = await shopApi.getUserLicenses();
      const ids = new Set((data || []).map((l) => l.itemId));
      set({ licenses: ids, isLoaded: true, isLoading: false });
    } catch (err) {
      console.warn('Failed to load licenses:', err);
      set({ isLoading: false });
    }
  },

  loadShopItems: async () => {
    if (get().shopItemsLoaded) return;
    try {
      const { shopApi } = await import('../services/shopApi');
      const items = await shopApi.getShopItems();
      const map = {};
      (items || []).forEach((item) => { map[item.itemId] = item; });
      set({ shopItemMap: map, shopItemsLoaded: true });
    } catch (err) {
      console.warn('Failed to load shop items for license gating:', err);
    }
  },

  addLicense: (itemId) => {
    set((state) => ({ licenses: new Set([...state.licenses, itemId]) }));
  },

  clearLicenses: () => {
    set({ licenses: new Set(), isLoaded: false, isLoading: false });
  },
}));
